import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  Snackbar,
  alpha,
  TablePagination,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FilterListIcon from '@mui/icons-material/FilterList';
import API from '../../api/axios';

const statusColors = {
  Pending: { color: '#f59e0b', bg: alpha('#f59e0b', 0.1) },
  Contacted: { color: '#3b82f6', bg: alpha('#3b82f6', 0.1) },
  Resolved: { color: '#10b981', bg: alpha('#10b981', 0.1) },
};

const StatusChip = ({ status }) => {
  const colors = statusColors[status] || { color: '#6b7280', bg: alpha('#6b7280', 0.1) };
  return (
    <Chip
      label={status}
      size="small"
      sx={{
        fontWeight: 600,
        color: colors.color,
        bgcolor: colors.bg,
        border: `1px solid ${alpha(colors.color, 0.2)}`,
        borderRadius: 1.5,
      }}
    />
  );
};

const ManageInquiries = () => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Confirm delete dialog
  const [deleteId, setDeleteId] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => { fetchInquiries(); }, []);

  const fetchInquiries = async () => {
    try {
      const { data } = await API.get('/inquiries');
      setInquiries(data);
    } catch (err) {
      setError('Failed to load inquiries');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (inquiryId, newStatus) => {
    try {
      await API.put(`/inquiries/${inquiryId}`, { status: newStatus });
      setInquiries((prev) => prev.map((i) => (i.id === inquiryId ? { ...i, status: newStatus } : i)));
      setSnackbar({ open: true, message: 'Status updated successfully', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to update status', severity: 'error' });
    }
  };

  const openDeleteDialog = (id) => { setDeleteId(id); setDeleteDialogOpen(true); };
  const closeDeleteDialog = () => { setDeleteId(null); setDeleteDialogOpen(false); };

  const handleDelete = async () => {
    try {
      await API.delete(`/inquiries/${deleteId}`);
      setInquiries((prev) => prev.filter((i) => i.id !== deleteId));
      setSnackbar({ open: true, message: 'Inquiry deleted successfully', severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: 'Failed to delete inquiry', severity: 'error' });
    } finally {
      closeDeleteDialog();
    }
  };

  const filteredInquiries = inquiries.filter((i) => {
    const matchesSearch =
      !search ||
      i.name?.toLowerCase().includes(search.toLowerCase()) ||
      i.email?.toLowerCase().includes(search.toLowerCase()) ||
      i.mobile?.toLowerCase().includes(search.toLowerCase()) ||
      i.destination?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'All' || i.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const paginatedInquiries = filteredInquiries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress sx={{ color: 'primary.main' }} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 4 } }}>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary', mb: 0.5 }}>
          Manage Inquiries
        </Typography>
        <Typography variant="body2" color="text.secondary">
          View and manage all callback requests
        </Typography>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>{error}</Alert>}

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <TextField
          size="small"
          placeholder="Search by name, email, phone..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: 'text.secondary', fontSize: 20 }} />
              </InputAdornment>
            ),
          }}
          sx={{ minWidth: 260, bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />
        <Select
          size="small"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
          startAdornment={
            <InputAdornment position="start">
              <FilterListIcon sx={{ color: 'text.secondary', fontSize: 20, ml: 1 }} />
            </InputAdornment>
          }
          sx={{ minWidth: 160, bgcolor: 'white', borderRadius: 2 }}
        >
          <MenuItem value="All">All Statuses</MenuItem>
          <MenuItem value="Pending">Pending</MenuItem>
          <MenuItem value="Contacted">Contacted</MenuItem>
          <MenuItem value="Resolved">Resolved</MenuItem>
        </Select>
      </Box>

      {/* Inquiries Table */}
      <Paper elevation={0} sx={{ border: '1px solid rgba(0,0,0,0.06)', borderRadius: 3, overflow: 'hidden' }}>
        <TableContainer>
          <Table sx={{ minWidth: 900 }}>
            <TableHead sx={{ bgcolor: '#f8fafc' }}>
              <TableRow>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary', py: 2 }}>Customer</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary', py: 2 }}>Contact Info</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary', py: 2 }}>Destination</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary', py: 2 }}>Date</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary', py: 2 }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary', py: 2 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedInquiries.length > 0 ? (
                paginatedInquiries.map((inquiry) => (
                  <TableRow key={inquiry.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} color="text.primary">
                        {inquiry.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{inquiry.email}</Typography>
                      <Typography variant="caption" color="text.secondary">{inquiry.mobile}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{inquiry.destination}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(inquiry.created_at).toLocaleDateString('en-IN', {
                          day: 'numeric', month: 'short', year: 'numeric'
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip status={inquiry.status} />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                        <Select
                          size="small"
                          value={inquiry.status}
                          onChange={(e) => handleStatusUpdate(inquiry.id, e.target.value)}
                          sx={{
                            height: 32,
                            fontSize: '0.8rem',
                            borderRadius: 1.5,
                            bgcolor: '#f8fafc',
                            '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(0,0,0,0.1)' },
                          }}
                        >
                          <MenuItem value="Pending" sx={{ fontSize: '0.8rem' }}>Set Pending</MenuItem>
                          <MenuItem value="Contacted" sx={{ fontSize: '0.8rem' }}>Set Contacted</MenuItem>
                          <MenuItem value="Resolved" sx={{ fontSize: '0.8rem' }}>Set Resolved</MenuItem>
                        </Select>
                        <IconButton
                          size="small"
                          onClick={() => openDeleteDialog(inquiry.id)}
                          sx={{ color: '#ef4444', bgcolor: alpha('#ef4444', 0.1), borderRadius: 1.5, '&:hover': { bgcolor: alpha('#ef4444', 0.2) } }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                    <Typography variant="body1" color="text.secondary">
                      No inquiries found matching your filters.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={filteredInquiries.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </Paper>

      {/* Delete Confirmation */}
      <Dialog open={deleteDialogOpen} onClose={closeDeleteDialog} PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Inquiry?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Are you sure you want to delete this inquiry? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDeleteDialog} sx={{ color: 'text.secondary', fontWeight: 600 }}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" sx={{ bgcolor: '#ef4444', fontWeight: 600, '&:hover': { bgcolor: '#dc2626' }, borderRadius: 2 }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notifications */}
      <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={snackbar.severity} sx={{ borderRadius: 2 }} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageInquiries;
